<template>
  <div class="flex flex-col h-full">
    <Teleport to="#topbar-search" defer>
      <SearchInput
        v-model="applicationsStore.searchQuery"
        placeholder="Search applications..."
        :debounce-time="500"
        @search="handleSearch"
        class="w-full"
      />
    </Teleport>
    <DashboardHeader title="All Applications" link="/applications/all">
      <template #right>
            <!-- Quick Filters -->
    <div class="flex justify-end mb-2 gap-2">
      <FilterButton
        title="All applications"
        :is-active="isAllApplicationsActive"
        :on-click="setAllApplications"
      />
      <FilterButton
        title="Ongoing only"
        :is-active="isOngoingOnlyActive"
        :on-click="setOngoingOnly"
      />
      <FilterButton
        title="Requests only"
        :is-active="isRequestsOnlyActive"
        :on-click="setRequestsOnly"
      />
    </div>
    </template>
    </DashboardHeader>


    <ApplicationStatusFilters
      class="mb-2"
      :show-application-status="true"
      :show-referral-status="true"
      :show-referral-type="true"
      :show-interview-status="true"
      :model-value="filterValues"
      @filter-change="handleFilterChange"
    />
    
    <!-- RESULTS SECTION -->
    <!-- Main content section - will scroll -->
    <div
      class="flex w-full flex-1 overflow-hidden border-[1px] border-gray-200 rounded-xl bg-white"
    >
      <div
        class="flex-[3] overflow-y-auto"
        :class="{ 'hidden xl:block': route.params.application_id }"
      >
        <KeepAlive :key="'applications-table'">
          <AllApplicationsTable :application-expanded="applicationExpanded" />
        </KeepAlive>
      </div>

      <!-- Application details section -->
      <router-view v-if="applicationExpanded" class="w-full flex-[5]" />
    </div>
  </div>

</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useApplicationsStore } from '@/stores/applications'

import SearchInput from '@/components/common/SearchInput.vue'
import AllApplicationsTable from '@/components/management/AllApplicationsTable.vue'
import DashboardHeader from '@/components/common/DashboardHeader.vue'
import ApplicationStatusFilters from '@/components/common/ApplicationStatusFilters.vue'
import FilterButton from '@/components/common/FilterButton.vue'

const applicationsStore = useApplicationsStore()
const route = useRoute()
const router = useRouter()

const handleSearch = () => {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  applicationsStore.fetchApplications()
}

const applicationExpanded = computed(() => {
  return route.params.application_id !== undefined
})

const filterValues = computed(() => ({
  applicationStatus: applicationsStore.selectedApplicationStatus || '',
  referralStatus: applicationsStore.selectedReferralStatus || '',
  referralType: applicationsStore.selectedReferralType || '',
  interviewStatus: applicationsStore.selectedInterviewStatus || ''
}))

// Quick filter active states
const isAllApplicationsActive = computed(() =>
  !applicationsStore.selectedApplicationStatus &&
  !applicationsStore.selectedReferralStatus &&
  !applicationsStore.selectedReferralType &&
  !applicationsStore.selectedInterviewStatus
)

const isOngoingOnlyActive = computed(() =>
  applicationsStore.selectedApplicationStatus === 'ONGOING' &&
  !applicationsStore.selectedReferralStatus &&
  !applicationsStore.selectedReferralType &&
  !applicationsStore.selectedInterviewStatus
)

const isRequestsOnlyActive = computed(() =>
  applicationsStore.selectedReferralStatus === 'READY_FOR_REFERRAL' &&
  !applicationsStore.selectedApplicationStatus &&
  !applicationsStore.selectedReferralType &&
  !applicationsStore.selectedInterviewStatus
)

const handleFilterChange = (filters: {
  applicationStatus?: string
  referralStatus?: string
  referralType?: string
  interviewStatus?: string
}) => {
  applicationsStore.selectedApplicationStatus = filters.applicationStatus || ''
  applicationsStore.selectedReferralStatus = filters.referralStatus || ''
  applicationsStore.selectedReferralType = filters.referralType || ''
  applicationsStore.selectedInterviewStatus = filters.interviewStatus || ''

  // Update URL with filter parameters
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  updateUrlAndFetch()
}

// Quick filter handlers (also sync URL and filter component via store)
const setAllApplications = () => {
  applicationsStore.selectedApplicationStatus = ''
  applicationsStore.selectedReferralStatus = ''
  applicationsStore.selectedReferralType = ''
  applicationsStore.selectedInterviewStatus = ''
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  updateUrlAndFetch()
}

const setOngoingOnly = () => {
  applicationsStore.selectedApplicationStatus = 'ONGOING'
  applicationsStore.selectedReferralStatus = ''
  applicationsStore.selectedReferralType = ''
  applicationsStore.selectedInterviewStatus = ''
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  updateUrlAndFetch()
}

const setRequestsOnly = () => {
  applicationsStore.selectedApplicationStatus = ''
  applicationsStore.selectedReferralStatus = 'READY_FOR_REFERRAL'
  applicationsStore.selectedReferralType = ''
  applicationsStore.selectedInterviewStatus = ''
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  updateUrlAndFetch()
}

const updateUrlAndFetch = async () => {
  const query: any = {
    page: 1,
    page_size: 50,
    ordering: '-updated_at',
    user_role: 'fellow'
  }

  // Add search if it exists in current route
  const currentSearch = route.query.search
  if (currentSearch && typeof currentSearch === 'string' && currentSearch.trim() !== '') {
    query.search = currentSearch
  }

  // Add filter parameters if they're not empty
  if (
    applicationsStore.selectedApplicationStatus &&
    applicationsStore.selectedApplicationStatus !== ''
  ) {
    query.application_status = applicationsStore.selectedApplicationStatus
  }
  if (applicationsStore.selectedReferralStatus && applicationsStore.selectedReferralStatus !== '') {
    query.referral_status = applicationsStore.selectedReferralStatus
  }
  if (applicationsStore.selectedReferralType && applicationsStore.selectedReferralType !== '') {
    query.referral_type = applicationsStore.selectedReferralType
  }
  if (
    applicationsStore.selectedInterviewStatus &&
    applicationsStore.selectedInterviewStatus !== ''
  ) {
    query.interview_status = applicationsStore.selectedInterviewStatus
  }

  await router.replace({ query })
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  applicationsStore.fetchApplications()
}

// Initialize filters from URL parameters on mount
onMounted(() => {
  const query = route.query

  // Apply application status filter
  if (query.application_status && typeof query.application_status === 'string') {
    applicationsStore.selectedApplicationStatus = query.application_status
  }

  // Apply referral status filter
  if (query.referral_status && typeof query.referral_status === 'string') {
    applicationsStore.selectedReferralStatus = query.referral_status
  }

  // Apply referral type filter
  if (query.referral_type && typeof query.referral_type === 'string') {
    applicationsStore.selectedReferralType = query.referral_type
  }

  // Apply interview stage filter
  if (query.interview_status && typeof query.interview_status === 'string') {
    applicationsStore.selectedInterviewStatus = query.interview_status
  }

  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  applicationsStore.fetchApplications()
})
</script>
